import React, { useCallback, useMemo } from "react";
import MuiInputField from "./MuiInputField";

/**
 * InputField – common V2 input; wraps MUI TextField (outlined) for all types.
 * Supports validation via validate(), transform/format, and all standard input props.
 *
 * @typedef {Object} InputFieldProps
 * @property {string} name
 * @property {"text"|"number"|"password"|"email"|"date"|"datetime-local"|"select"|"textarea"} [type="text"]
 * @property {any} value
 * @property {(value: any) => void} onChange
 * @property {string} [label]
 * @property {string} [title] – alias of label
 * @property {React.ReactNode} [startAdornment]
 * @property {React.ReactNode} [endAdornment]
 * @property {string} [placeholder]
 * @property {boolean} [disabled]
 * @property {boolean} [required]
 * @property {boolean} [readOnly]
 * @property {string} [error]
 * @property {string} [helperText]
 * @property {number|string} [min]
 * @property {number|string} [max]
 * @property {number} [minLength]
 * @property {number} [maxLength]
 * @property {string} [pattern]
 * @property {{ label: string; value: string|number }[]} [options]
 * @property {boolean} [multiple]
 * @property {number} [rows]
 * @property {string} [className]
 * @property {string} [inputClassName]
 * @property {string} [labelClassName]
 * @property {(value: any) => any} [transform]
 * @property {(value: any) => any} [format]
 * @property {(value: any) => string|null} [validate]
 * @property {boolean} [placeholderOnly]
 */
export default function InputField(props) {
  const {
    type = "text",
    value,
    onChange,
    transform,
    format,
    validate,
    error: errorProp,
    title,
    label,
    required,
    ...rest
  } = props;

  const transformedValue = useMemo(() => {
    if (format && value !== undefined && value !== null && value !== "") {
      try {
        return format(value);
      } catch {
        return value;
      }
    }
    return value;
  }, [format, value]);

  const handleChange = useCallback(
    (newValue) => {
      let next = newValue;
      if (transform) {
        try {
          next = transform(next);
        } catch {
          // keep next as-is
        }
      }
      onChange(next);
    },
    [onChange, transform]
  );

  const validationError = useMemo(() => {
    if (!validate) return null;
    try {
      return validate(value);
    } catch {
      return null;
    }
  }, [validate, value]);

  const error = errorProp ?? validationError ?? undefined;

  const isDateLike = type === "date" || type === "datetime-local";
  const effectiveLabel = label ?? title ?? (isDateLike ? rest.placeholder : undefined);
  const effectivePlaceholder =
    isDateLike && !label && !title ? undefined : rest.placeholder;

  // Single required indicator: MuiInputField passes `required` to MUI TextField, which adds the asterisk.
  // Do not append " *" to the label or labels show two stars.

  return (
    <MuiInputField
      {...rest}
      type={type}
      placeholder={effectivePlaceholder}
      value={transformedValue}
      onChange={handleChange}
      error={error}
      label={effectiveLabel}
      required={required}
    />
  );
}
